import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import connectUser from '../../hive/connectUser';
import Icon from '../../hive/ui/Icon';
import FairTopBar from './shell/FairTopBar';
import FairTabBar from './shell/FairTabBar';
import FairTrackerProvider, { useFairTracker } from './data/FairTrackerStore';
import { I18nProvider, tr, preloadStrings } from './i18n';
import { register as registerSW, isInstalled } from './pwa/swRegister';
import useInstallable from './pwa/useInstallable';
import { requestPersistence } from './data/db';
import { ToastHost } from './ui';
import { FT_ROUTES, BASE, chromeFor } from './routes';
import './fonts/manrope.css';
import './fairTracker.css';
import './fairTrackerScreens.css';
import './fairTrackerResponsive.css';

// Registers window.__fairTrackerSelfCheck for the IndexedDB / outbox round-trip
// check. Inside a NODE_ENV guard so minification drops it from the production
// bundle entirely rather than shipping a dev tool.
if (process.env.NODE_ENV === 'development') {
  require('./data/selfcheck');
}

/**
 * Fair Tracker — route host.
 *
 * Mounted once at /fair-tracker by routes/index.js and owns every screen below
 * that path through this <Switch>. Base.jsx renders protected app routes without
 * `exact`, so the prefix match arrives here intact; one route id in the registry
 * therefore covers all 16 screens.
 *
 * The Hive top bar sits ABOVE this and owns the app title and the back arrow —
 * the same as every other BeeNexa app. FairTopBar adds a short context strip
 * for the things Hive's bar cannot know about (active fair, language, settings).
 */
function FairTrackerShell() {
  const location = useLocation();
  const { settings, fair, loaded, storageError, dismissStorageError } = useFairTracker();
  const chrome = chromeFor(location.pathname);
  const [update, setUpdate] = useState(null);
  // Swaps the page manifest to Fair Tracker's so an install lands on this app.
  useInstallable();

  // 'auto' leaves the attribute off so prefers-color-scheme decides (spec § 2).
  const theme = settings && settings.theme;
  const themeAttr = theme && theme !== 'auto' ? theme : undefined;
  const lang = (settings && settings.language) || 'en';

  /* Install the service worker the first time someone opens Fair Tracker — it
     is what lets the app cold-boot with no network. Nothing else in BeeNexa
     calls register(), so other users never get one. */
  /* Let the app's background cover the whole host. HiveShell's <main> paints
     a white canvas and is flex-sized, so .ft-root's min-height:100% resolves
     against nothing and the grey stops partway down the page. Tagging the host
     makes it a flex column so .ft-root can grow into it. Removed on unmount so
     no other app is affected. */
  useEffect(() => {
    const host = document.querySelector('main.hive-content');
    if (!host) return undefined;
    host.classList.add('ft-host');
    return () => host.classList.remove('ft-host');
  }, []);

  useEffect(() => {
    registerSW({ onUpdate: (reload) => setUpdate(reload) });
    // Ask for persistent storage once installed, which is when the browser is
    // most likely to grant it. On iOS this is what stops site data being
    // evicted after a week unused — on a five-day trip that is the whole trip.
    if (isInstalled()) requestPersistence();
  }, []);

  /**
   * Warm the translation chunk while there is still a connection.
   *
   * The table is code-split, and the service worker only caches a build asset
   * AFTER it has been fetched once. A buyer who first reaches for Chinese at
   * the booth — offline, which is the whole premise — would otherwise hit a
   * failed chunk fetch and silently stay in English. 34KB once, on idle, buys
   * the language switch working for the rest of the trip.
   *
   * Deliberately not gated on the current language: the point is to cover the
   * switch that has not happened yet.
   */
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return undefined;
    const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 2000));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const handle = idle(() => preloadStrings());
    return () => cancel(handle);
  }, []);

  return (
    <I18nProvider lang={lang}>
      <div className="ft-root" data-ft-theme={themeAttr}>
        {chrome !== 'none' && (
          <FairTopBar fairLabel={fair ? fair.name : null} lang={lang.toUpperCase()} />
        )}

        {update && (
          <div className="ft-banner ft-banner-info" role="status">
            <Icon name="RefreshCw" size={15} />
            <span>{tr("A newer version of BeeNexa is ready.")}</span>
            <button type="button" className="ft-btn ft-btn-sm" onClick={update}>
              {tr("Reload")}
            </button>
            <button type="button" onClick={() => setUpdate(null)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        )}

        {storageError && (
          <div className="ft-banner ft-banner-error" role="alert">
            <Icon name="AlertTriangle" size={15} />
            <span>{storageError}</span>
            <button type="button" onClick={dismissStorageError} aria-label="Dismiss">
              ✕
            </button>
          </div>
        )}

        {/* Screens render against the local database, so this wait is a few
            milliseconds of IndexedDB — not a network round trip. */}
        {loaded ? (
          <Switch>
            {FT_ROUTES.map((r) => (
              <Route key={r.key} path={r.path} exact={r.exact} component={r.component} />
            ))}
            {/* Anything unrecognised under /fair-tracker lands on the dashboard
                rather than BeeNexa's 404 — the app owns this sub-tree. */}
            <Route render={() => <Redirect to={BASE} />} />
          </Switch>
        ) : (
          <div className="ft-page" aria-busy="true" />
        )}

        {chrome === 'full' && <FairTabBar />}
      </div>
    </I18nProvider>
  );
}

function FairTracker({ userInfo }) {
  // ToastHost sits OUTSIDE the provider's children tree root so a toast
  // survives the screen that raised it unmounting — "Supplier saved" has to
  // still be visible after Quick Entry's Save has navigated on to My Suppliers.
  return (
    <ToastHost>
      <FairTrackerProvider userInfo={userInfo}>
        <FairTrackerShell />
      </FairTrackerProvider>
    </ToastHost>
  );
}

export default connectUser(FairTracker);
